import { useForm } from '@conform-to/react';
import { parse } from '@conform-to/zod';
import { cssBundleHref } from '@remix-run/css-bundle';
import {
  json,
  type DataFunctionArgs,
  type HeadersFunction,
  type LinksFunction,
  type MetaFunction } from
'@remix-run/node';
import {
  Form,
  Link,
  Links,
  LiveReload,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useFetcher,
  useFetchers,
  useLoaderData,
  useLocation,
  useMatches,
  useSubmit } from
'@remix-run/react';
import { withSentry } from '@sentry/remix';
import * as React from 'react';
import { useRef } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { ClientOnly } from 'remix-utils/client-only';
import { z } from 'zod';
import * as fbq from '#app/utils/ftag.client.tsx';
import * as gtags from '#app/utils/gtags.client.tsx';
import {
  cn,
  combineHeaders,
  getDomainUrl,
  invariantResponse } from
'#app/utils/misc.tsx';
import { cannyTracking } from './components/canny.js';
import { CookieConsentBanner } from './components/cookies.tsx';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import { Footer } from './components/footer.tsx';
import { ErrorList } from './components/forms.tsx';
import { appName } from './components/text.ts';
import { EpicToaster } from './components/toaster.tsx';
import { Button } from './components/ui/button.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger } from
'./components/ui/dropdown-menu.tsx';
import { Icon, href as iconsHref } from './components/ui/icon.tsx';
import { AppIcon } from './components/ui/icons/app-icon.tsx';
import fontStylestylesheetUrl from './styles/font.css';
import sonnerStyleSheet from './styles/sonner.css';
import tailwindStylesheetUrl from './styles/tailwind.css';
import { authenticator, getUserId } from './utils/auth.server.ts';
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx';
import { prisma } from './utils/db.server.ts';
import { getEnv } from './utils/env.server.ts';
import {
  removeReferrerFromLocalStorageOnSignupSuccess,
  saveQueryParamToLocalStorage } from
'./utils/local-storage.ts';
import { useNonce } from './utils/nonce-provider.ts';
import { useRequestInfo } from './utils/request-info.ts';
import { type Theme, setTheme, getTheme } from './utils/theme.server.ts';
import { makeTimings, time } from './utils/timing.server.ts';
import { getToast } from './utils/toast.server.ts';
import { useOptionalUser, useUser } from './utils/user.ts';

export const links: LinksFunction = () => {
  return [
  // Preload svg sprite as a resource to avoid render blocking
  { rel: 'preload', href: iconsHref, as: 'image' },
  // Preload CSS as a resource to avoid render blocking
  { rel: 'preload', href: fontStylestylesheetUrl, as: 'style' },
  { rel: 'preload', href: tailwindStylesheetUrl, as: 'style' },
  cssBundleHref ? { rel: 'preload', href: cssBundleHref, as: 'style' } : null,
  { rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
  {
    rel: 'alternate icon',
    type: 'image/png',
    href: '/favicons/favicon-32x32.png'
  },
  { rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' }, (
  {
    rel: 'manifest',
    href: '/site.webmanifest',
    crossOrigin: 'use-credentials'
  } as const), // necessary to make typescript happy
  //These should match the css preloads above to avoid css as render blocking resource
  { rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
  { rel: 'stylesheet', href: fontStylestylesheetUrl },
  { rel: 'stylesheet', href: tailwindStylesheetUrl },
  { rel: 'stylesheet', href: sonnerStyleSheet },
  cssBundleHref ? { rel: 'stylesheet', href: cssBundleHref } : null].
  filter(Boolean);
};

let siteDescription =
'Create funny, weird and amazing face swaps with any picture. No Photosip skills required. Our AI will do the magic for you';

let ogImage = 'https://swapmyface.app/img/og-image.jpg';

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
  {
    title: data ?
    appName + ': Put your face in any picture' :
    'Error | ' + appName
  },
  {
    name: 'description',
    content: siteDescription
  },
  { name: 'twitter:card', content: 'summary_large_image' },
  { name: 'twitter:site', content: '@swapmyface' },
  { name: 'twitter:creator', content: '@swapmyface' },
  {
    name: 'twitter:title',
    content: 'SwapMyFace AI: Face Swap Online - AI Face Swapper'
  },
  { name: 'twitter:description', content: siteDescription },
  { name: 'twitter:image', content: ogImage },
  { property: 'og:image', content: ogImage },
  { property: 'og:image2', content: ogImage }];

};

export async function loader({ request }: DataFunctionArgs) {
  const timings = makeTimings('root loader');
  const userId = await time(() => getUserId(request), {
    timings,
    type: 'getUserId',
    desc: 'getUserId in root'
  });

  const user = userId ?
  await time(
    () =>
    prisma.user.findUniqueOrThrow({
      select: {
        id: true,
        name: true,
        username: true,
        email: true,
        image: { select: { id: true } },
        roles: {
          select: {
            name: true,
            permissions: {
              select: { entity: true, action: true, access: true }
            }
          }
        }
      },
      where: { id: userId }
    }),
    { timings, type: 'find user', desc: 'find user in root' }
  ) :
  null;
  if (userId && !user) {
    console.info('something weird happened');
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await authenticator.logout(request, { redirectTo: '/' });
  }
  const { toast, headers: toastHeaders } = await getToast(request);

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request)
        }
      },
      ENV: getEnv(),
      gaTrackingId: process.env.GA_TRACKING_ID,
      toast
    },
    {
      headers: combineHeaders(
        { 'Server-Timing': timings.toString() },
        toastHeaders
      )
    }
  );
}

export type RootLoaderType = typeof loader;

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    'Server-Timing': loaderHeaders.get('Server-Timing') ?? ''
  };
  return headers;
};

const ThemeFormSchema = z.object({
  theme: z.enum(['system', 'light', 'dark'])
});

export async function action({ request }: DataFunctionArgs) {
  const formData = await request.formData();
  invariantResponse(
    formData.get('intent') === 'update-theme',
    'Invalid intent',
    { status: 400 }
  );
  const submission = parse(formData, {
    schema: ThemeFormSchema
  });
  if (submission.intent !== 'submit') {
    return json(({ status: 'success', submission } as const));
  }
  if (!submission.value) {
    return json(({ status: 'error', submission } as const), { status: 400 });
  }
  const { theme } = submission.value;

  const responseInit = {
    headers: { 'set-cookie': setTheme(theme) }
  };
  return json({ success: true, submission }, responseInit);
}

function Document({
  children,
  nonce,
  theme = 'dark',
  origin,
  env = {},
  gaTrackingId







}: {children: React.ReactNode;nonce: string;theme?: Theme;origin?: string;env?: Record<string, string>;gaTrackingId?: string;}) {
  const location = useLocation();
  React.useEffect(() => {
    if (gaTrackingId?.length) {
      gtags.pageview(location.pathname, gaTrackingId);
    }
  }, [location, gaTrackingId]);

  React.useEffect(() => {
    fbq.pageview();
  }, [location]);

  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<meta name="_foundr" content="eae9b23d9df50ae00c1dac83e310f6af" />
				<Meta />
				<ClientHintCheck nonce={nonce} />
				<link href={origin + location.pathname} rel="canonical" />
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.lemonSqueezyAffiliateConfig = { store: "luisiacc" };`
          }}>
        </script>
				<script src="https://lmsqueezy.com/affiliate.js" defer></script>
				{/*<script src="https://assets.lemonsqueezy.com/lemon.js" defer></script> */}
				<meta
          name="google-site-verification"
          content="L55d_6p28JHYfj3lKSTUElXotqYb5h373AO4YZzd33A" />

				<Links />
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `
          !function(w,d){if(!w.rdt){var p=w.rdt=function(){p.sendEvent?p.sendEvent.apply(p,arguments):p.callQueue.push(arguments)};p.callQueue=[];var t=d.createElement("script");t.src="https://www.redditstatic.com/ads/pixel.js",t.async=!0;var s=d.getElementsByTagName("script")[0];s.parentNode.insertBefore(t,s)}}(window,document);rdt('init','a2_dnw0o1z9dp53', {"optOut":false,"useDecimalCurrencyValues":true,"email":"<EMAIL-HERE>"});rdt('track', 'PageVisit');
          `
          }}>
        </script>
			</head>
			<body className="bg-background text-foreground">
				{process.env.NODE_ENV === 'development' || !gaTrackingId ? null :
        <>
						<script
            async
            src={`https://www.googletagmanager.com/gtag/js?id=${gaTrackingId}`} />

						<script
            async
            id="gtag-init"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', '${gaTrackingId}', {
                  page_path: window.location.pathname,
                });
              `
            }} />

					</>}

				<script
          async
          id="fbq-pixel-init"
          dangerouslySetInnerHTML={{
            __html: `
              !(function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)}; if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0'; n.queue=[];t=b.createElement(e);t.async=!0; t.src=v;s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s)})(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '414925534698742');
              `
          }} />

				<noscript>
					<img
            height="1"
            width="1"
            style={{ display: 'none' }}
            src="https://www.facebook.com/tr?id=414925534698742&ev=PageView&noscript=1" />

				</noscript>
				{children}
				<script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`
          }} />


				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				<LiveReload nonce={nonce} />
			</body>
		</html>);

}

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const user = useOptionalUser();
  const matches = useMatches();
  let currentMatch = matches.slice(-1)[0];
  let theme = useTheme();

  saveQueryParamToLocalStorage('referrer');
  saveQueryParamToLocalStorage('aff');
  removeReferrerFromLocalStorageOnSignupSuccess();

  let isIndex = currentMatch?.id === 'routes/_marketing+/index';
  if (isIndex) {
    // force dark theme on homepage
    theme = 'dark';
  }

  React.useEffect(() => {
    if (user) {
      cannyTracking();
      // @ts-ignore
      Canny?.('identify', {
        appID: '6524a3166333c331d3a99d97',
        user: {
          // Replace these values with the current user's data
          email: user.email,
          name: user.username,
          id: user.id
        }
      });
    }
  }, [user]);

  return (
    <Document
      gaTrackingId={data.gaTrackingId}
      origin={data.requestInfo.origin}
      nonce={nonce}
      theme={theme}
      env={data.ENV}>

			<div className="flex flex-col justify-between">
				<div
          className={cn('w-full', {
            'fixed z-50 bg-background/70': isIndex
          })}>

					<header className="container pb-2 pt-3">
						<nav className="flex items-center justify-between">
							<span className="flex flex-row items-center gap-6">
								<Link to="/" className="flex flex-row items-center gap-2">
									<AppIcon className="h-10 w-10" />
									<b className="bg-app-gradient mt-1 bg-clip-text text-h6 text-transparent bg-blend-normal">
										SwapMyFace AI
									</b>
								</Link>
							</span>
							<div className="flex items-center justify-center gap-8">
								{isIndex &&
                <div className="hidden flex-row items-center gap-8 md:flex">
										<Link to="/plans">Pricing</Link>
										<Link to="#faqs">FAQ</Link>
									</div>}

								{user ?
                <>
										<ThemeSwitch
                    userPreference={data.requestInfo.userPrefs.theme} />

										<UserDropdown />
									</> :

                <Link
                  prefetch="viewport"
                  to="/login"
                  className="bg-app-gradient rounded-xl py-2 pl-8 pr-6">

										<b>Log In</b>
										<Icon className="ml-4" name="arrow-right" />
									</Link>}

							</div>
						</nav>
					</header>
				</div>

				<div className="block pb-6">
					<GoogleReCaptchaProvider
            container={{
              parameters: {
                badge: undefined
              }
            }}
            reCaptchaKey={data.ENV.GOOGLE_RECAPTCHA_SITEKEY ?? ''}>

						<Outlet />
					</GoogleReCaptchaProvider>
				</div>
			</div>
			<EpicToaster toast={data.toast} />
			<Footer />
			<ClientOnly>{() => <CookieConsentBanner />}</ClientOnly>
			<div id="modal-root"></div>
		</Document>);

}
export default withSentry(App);

function UserDropdown() {
  const user = useUser();
  const submit = useSubmit();
  const formRef = useRef<HTMLFormElement>(null);

  let username =
  // crop the username if it's too long
  <span className="max-w-[100px] truncate text-body-sm font-bold">
			{user.name ?? user.username}
		</span>;


  return (
    <DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button asChild variant="secondary">
					<Link
            to="/settings/profile"
            // this is for progressive enhancement
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2">

						{username}
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="end">
					<DropdownMenuItem asChild>
						<Link prefetch="viewport" to="/me">
							<Icon className="text-body-md" name="image">
								Go to app
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to="/settings/profile">
							<Icon className="text-body-md" name="user">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<a
              href="https://swapmyface.canny.io/feature-requests"
              target="_blank"
              rel="noreferrer">

							<Icon name="rocket">
								<span>Request a feature</span>
							</Icon>
						</a>
					</DropdownMenuItem>
					<DropdownMenuItem
            asChild
            // this prevents the menu from closing before the form submission is completed
            onSelect={(event) => {
              event.preventDefault();
              submit(formRef.current);
            }}>

						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="exit">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>);

}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
  const hints = useHints();
  const requestInfo = useRequestInfo();
  const optimisticMode = useOptimisticThemeMode();
  if (optimisticMode) {
    return optimisticMode === 'system' ? hints.theme : optimisticMode;
  }
  return requestInfo.userPrefs.theme ?? hints.theme;
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
  const fetchers = useFetchers();

  const themeFetcher = fetchers.find(
    (f) => f.formData?.get('intent') === 'update-theme'
  );

  if (themeFetcher && themeFetcher.formData) {
    const submission = parse(themeFetcher.formData, {
      schema: ThemeFormSchema
    });
    return submission.value?.theme;
  }
}

function ThemeSwitch({ userPreference }: {userPreference?: Theme | null;}) {
  const fetcher = useFetcher<typeof action>();

  const [form] = useForm({
    id: 'theme-switch',
    lastSubmission: fetcher.data?.submission,
    onValidate({ formData }) {
      return parse(formData, { schema: ThemeFormSchema });
    }
  });

  const optimisticMode = useOptimisticThemeMode();
  const mode = optimisticMode ?? userPreference ?? 'system';
  const nextMode =
  mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system';
  const modeLabel = {
    light:
    <Icon name="sun">
				<span className="sr-only">Light</span>
			</Icon>,

    dark:
    <Icon name="moon">
				<span className="sr-only">Dark</span>
			</Icon>,

    system:
    <Icon name="laptop">
				<span className="sr-only">System</span>
			</Icon>

  };

  return (
    <fetcher.Form method="POST" {...form.props}>
			<input type="hidden" name="theme" value={nextMode} />
			<div className="flex gap-2">
				<button
          name="intent"
          value="update-theme"
          type="submit"
          className="flex h-8 w-8 cursor-pointer items-center justify-center">

					{modeLabel[mode]}
				</button>
			</div>
			<ErrorList errors={form.errors} id={form.errorId} />
		</fetcher.Form>);

}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>);

}